import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import './Reports.css';

import AttendanceStats from './Reports/AttendanceStats';
import FridgeRentals from './Reports/FridgeRentals';
import SharedAccommodations from './Reports/SharedAccommodations';
import PendingRegistrations from './Reports/PendingRegistrations';
import PartialAvailability from './Reports/PartialAvailability';
import PaymentRecords from './Reports/PaymentRecords';
import AccountsReceivable from './Reports/AccountsReceivable';
import CampMasterList from './Reports/CampMasterList';
import YouthBreakDown from './Reports/YouthBreakDown';
import Arrived from './Reports/Arrived';
import TreasurersAnnualReport from './Reports/TreasurersAnnualReport';
import TempFinancialSummary from './Reports/TempFinancialSummary';
import Donations from './Reports/Donations';
import OtherFeesReport from './Reports/OtherFeesReport';
import ProgramInterests from './Reports/ProgramInterests';
import MealTickets from './Reports/MealTickets';
import PrimaryCampersContact from './Reports/PrimaryCampersContact';
import SecurityLogs from './Reports/SecurityLogs';
import SubmissionErrorLogs from './Reports/SubmissionErrorLogs';
import EtransferInboxReport from './Reports/EtransferInboxReport';
import ParBalancesReport from './Reports/ParBalancesReport';

import NewCamperReport from './Reports/NewCamperReport';
import CabinUtilization from './Reports/CabinUtilization';

interface EmailLog {
  id: string;
  to: string;
  subject: string;
  content: string;
  status: 'sent' | 'failed';
  timestamp: string;
  originalTimestamp: Date | null;
  error?: string;
}

type EmailSortKey = 'timestamp' | 'to' | 'subject' | 'status';
type ReportType = 'logs' | 'email' | 'securityLogs' | 'submissionErrorLogs' | 'attendanceStats' | 'fridgeRentals' | 'sharedAccommodations' | 'pendingRegistrations' | 'partialAvailability' | 'paymentRecords' | 'etransferInbox' | 'parBalances' | 'newCamperReport' | 'accountsReceivable' | 'campMasterList' | 'youthBreakDown' | 'arrived' | 'treasurersAnnualReport' | 'tempFinancialSummary' | 'donations' | 'otherFees' | 'programInterests' | 'mealTickets' | 'primaryCampersContact' | 'cabinUtilization' | null;
type ReportsLocationState = {
  resetToReportsList?: boolean;
  openReport?: Exclude<ReportType, null>;
};

const Reports: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedReport, setSelectedReport] = useState<ReportType>(null);
  const [emailSortConfig, setEmailSortConfig] = useState<{
    key: EmailSortKey;
    direction: 'asc' | 'desc';
  }>({ key: 'timestamp', direction: 'desc' });
  const [emailSearchTerm, setEmailSearchTerm] = useState('');
  
  const [emailLogsLoading, setEmailLogsLoading] = useState(true);
  const [emailLogsError, setEmailLogsError] = useState('');
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);

  useEffect(() => {
    const state = (location.state as ReportsLocationState | null) || null;
    const requestedReport = state?.openReport;
    if (requestedReport) {
      setSelectedReport(requestedReport);
      setSelectedEmail(null);
      return;
    }

    const shouldReset = Boolean(state?.resetToReportsList);
    if (shouldReset) {
      setSelectedReport(null);
      setSelectedEmail(null);
    }
  }, [location.key, location.state]);

  useEffect(() => {
    if (selectedReport === 'email') {
      fetchEmailLogs();
    }
  }, [selectedReport]);

  const fetchEmailLogs = async () => {
    try {
      setEmailLogsLoading(true);
      setEmailLogsError('');
      const emailLogsRef = collection(db, 'emails');
      const emailLogsSnapshot = await getDocs(emailLogsRef);
      
      console.log('Raw email logs:', emailLogsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      const emailLogsData = emailLogsSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Processing email doc:', { id: doc.id, data });
        
        // Handle Firestore timestamp - check all possible timestamp fields
        let timestamp = 'Unknown';
        let originalTimestamp: Date | null = null;

        // Try to get the original timestamp
        const getTimestamp = (field: any): Date | null => {
          if (field?.toDate instanceof Function) {
            return field.toDate();
          } else if (field instanceof Date) {
            return field;
          }
          return null;
        };

        // Check all possible timestamp fields
        const timestampFields = [data.timestamp, data.sentAt, data.createdAt, data.date];
        for (const field of timestampFields) {
          const date = getTimestamp(field);
          if (date) {
            originalTimestamp = date;
            timestamp = date.toLocaleString();
            break;
          }
        }

        // Handle recipient(s) - try to extract email from nested objects
        let recipient = '';
        const extractEmail = (obj: any): string => {
          if (!obj) return '';
          if (typeof obj === 'string') return obj;
          if (Array.isArray(obj)) return obj.map(extractEmail).filter(Boolean).join(', ');
          if (typeof obj === 'object') {
            // Check common email fields
            if (obj.email) return obj.email;
            if (obj.emailAddress) return obj.emailAddress;
            if (obj.address) return obj.address;
            // If no email field found, try all string values
            return Object.values(obj)
              .map(val => extractEmail(val))
              .filter(Boolean)[0] || '';
          }
          return '';
        };

        // Try to find recipient in common fields
        const possibleRecipientFields = ['to', 'recipient', 'recipients', 'email', 'emailAddress', 'address'];
        for (const field of possibleRecipientFields) {
          const extracted = extractEmail(data[field]);
          if (extracted) {
            recipient = extracted;
            break;
          }
        }

        // Handle status with proper default to 'sent'
        const status = data.status === 'failed' ? 'failed' : 'sent';
        
        // Handle content - check multiple possible content fields and formats
        let content = '';
        const extractContent = (obj: any): string => {
          if (!obj) return '';
          if (typeof obj === 'string') return obj;
          if (typeof obj === 'object') {
            // If it's an object with html/text fields (common in email)
            if (obj.html) return obj.html;
            if (obj.text) return obj.text;
            if (obj.body) return obj.body;
            if (obj.message) return obj.message;
            // Try to stringify the object nicely
            try {
              return JSON.stringify(obj, null, 2);
            } catch {
              return String(obj);
            }
          }
          return String(obj);
        };

        // Check common content field names
        const possibleContentFields = ['content', 'body', 'html', 'text', 'message', 'emailContent', 'messageBody'];
        for (const field of possibleContentFields) {
          if (data[field]) {
            const extracted = extractContent(data[field]);
            if (extracted) {
              content = extracted;
              break;
            }
          }
        }

        // If no content found in common fields, try template fields
        if (!content && data.template) {
          content = extractContent(data.template);
        }

        // If still no content, check for data fields that might contain the content
        if (!content && data.data) {
          content = extractContent(data.data);
        }
        
        const emailLog: EmailLog = {
          id: doc.id,
          to: recipient || 'No recipient',
          subject: String(data.subject || ''),
          content: content || 'No content available',
          status: status,
          timestamp: timestamp,
          originalTimestamp: originalTimestamp,
          error: data.error ? String(data.error) : undefined
        };
        
        console.log('Processed email log:', emailLog);
        return emailLog;
      });
      
      // Initial sort by timestamp
      const sortedLogs = emailLogsData.sort((a, b) => {
        if (!a.originalTimestamp && !b.originalTimestamp) return 0;
        if (!a.originalTimestamp) return 1;
        if (!b.originalTimestamp) return -1;
        return b.originalTimestamp.getTime() - a.originalTimestamp.getTime();
      });
      
      console.log('Final sorted logs:', sortedLogs);
      setEmailLogs(sortedLogs);
    } catch (err) {
      console.error('Error fetching email logs:', err);
      setEmailLogsError('Failed to load email logs');
    } finally {
      setEmailLogsLoading(false);
    }
  };

  const sortEmails = (key: EmailSortKey) => {
    setEmailSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedEmails = () => {
    if (!Array.isArray(emailLogs)) return [];
    
    // First filter by search term
    const filteredEmails = emailLogs.filter(email => 
      email.to.toLowerCase().includes(emailSearchTerm.toLowerCase())
    );
    
    // Then sort the filtered results
    return [...filteredEmails].sort((a, b) => {
      const direction = emailSortConfig.direction === 'asc' ? 1 : -1;
      
      switch (emailSortConfig.key) {
        case 'timestamp':
          if (!a.originalTimestamp && !b.originalTimestamp) return 0;
          if (!a.originalTimestamp) return 1;
          if (!b.originalTimestamp) return -1;
          return direction * (a.originalTimestamp.getTime() - b.originalTimestamp.getTime());
        
        case 'to':
          return direction * (a.to || '').localeCompare(b.to || '');
        
        case 'subject':
          return direction * (a.subject || '').localeCompare(b.subject || '');
        
        case 'status':
          return direction * (a.status || '').localeCompare(b.status || '');
        
        default:
          return 0;
      }
    });
  };

  const renderEmailLogs = () => {
    if (selectedEmail) {
      return (
        <div className="report-section">
          <div className="email-detail-full">
            <div className="email-detail-header">
              <h2>Email Details</h2>
              <button 
                className="back-button"
                onClick={() => setSelectedEmail(null)}
              >
                Back to Email List
              </button>
            </div>
            <div className="email-detail-content">
              <div className="email-metadata">
                <div className="metadata-row">
                  <div className="metadata-label">Date:</div>
                  <div className="metadata-value">{selectedEmail.timestamp || 'Unknown'}</div>
                </div>
                <div className="metadata-row">
                  <div className="metadata-label">To:</div>
                  <div className="metadata-value">{selectedEmail.to || 'No recipient'}</div>
                </div>
                <div className="metadata-row">
                  <div className="metadata-label">Subject:</div>
                  <div className="metadata-value">{selectedEmail.subject || 'No subject'}</div>
                </div>
                <div className="metadata-row">
                  <div className="metadata-label">Status:</div>
                  <div className="metadata-value">
                    <span className={`status-badge ${selectedEmail.status || 'unknown'}`}>
                      {selectedEmail.status || 'unknown'}
                    </span>
                  </div>
                </div>
                {selectedEmail.error && (
                  <div className="metadata-row">
                    <div className="metadata-label">Error:</div>
                    <div className="metadata-value error-message">{selectedEmail.error}</div>
                  </div>
                )}
              </div>
              <div className="email-content-section">
                <h3>Email Content</h3>
                <div 
                  className="content-box"
                  dangerouslySetInnerHTML={{ __html: selectedEmail.content }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="report-section">
        <h2>Email Log</h2>
        <div className="email-search-container">
          <input
            type="text"
            placeholder="Search by email address..."
            value={emailSearchTerm}
            onChange={(e) => setEmailSearchTerm(e.target.value)}
            className="email-search-input"
          />
        </div>
        <div className="email-list-container">
          <table className="email-table">
            <thead>
              <tr>
                <th onClick={() => sortEmails('timestamp')}>
                  Date {emailSortConfig.key === 'timestamp' && (emailSortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => sortEmails('to')}>
                  Recipient {emailSortConfig.key === 'to' && (emailSortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => sortEmails('subject')}>
                  Subject {emailSortConfig.key === 'subject' && (emailSortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => sortEmails('status')}>
                  Status {emailSortConfig.key === 'status' && (emailSortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {getSortedEmails().map(email => (
                <tr 
                  key={email.id} 
                  onClick={() => setSelectedEmail(email)}
                  className="email-row"
                >
                  <td>{email.timestamp || 'Unknown'}</td>
                  <td>{email.to || 'No recipient'}</td>
                  <td>{email.subject || 'No subject'}</td>
                  <td>
                    <span className={`status-badge ${email.status || 'unknown'}`}>
                      {email.status || 'unknown'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderLogsList = () => {
    return (
      <div className="reports-list">
        <h2>Logs</h2>
        <div className="report-cards">
          <div
            className={`report-card rainbow-red ${selectedReport === 'email' ? 'active' : ''}`}
            onClick={() => setSelectedReport('email')}
          >
            <h3>Email Logs</h3>
            <p>View all system-generated emails and their delivery status</p>
          </div>

          <div
            className={`report-card rainbow-orange ${selectedReport === 'securityLogs' ? 'active' : ''}`}
            onClick={() => setSelectedReport('securityLogs')}
          >
            <h3>Security Logs</h3>
            <p>View authentication and account security events from auth logs</p>
          </div>

          <div
            className={`report-card rainbow-blue ${selectedReport === 'submissionErrorLogs' ? 'active' : ''}`}
            onClick={() => setSelectedReport('submissionErrorLogs')}
          >
            <h3>Submission Error Logs</h3>
            <p>View registration submission failures with error codes and context</p>
          </div>
        </div>
      </div>
    );
  };

  const renderReportsList = () => {
    return (
      <div className="reports-list">
        <h2>Available Reports</h2>
        <div className="report-cards">
          <div 
            className={`report-card rainbow-red ${selectedReport === 'logs' ? 'active' : ''}`}
            onClick={() => setSelectedReport('logs')}
          >
            <h3>Logs</h3>
            <p>Browse email, security, and submission error logs</p>
          </div>

          <div 
            className={`report-card rainbow-orange ${selectedReport === 'attendanceStats' ? 'active' : ''}`}
            onClick={() => setSelectedReport('attendanceStats')}
          >
            <h3>Attendance Stats</h3>
            <p>View registration statistics by age category and grade level</p>
          </div>
          <div 
            className={`report-card rainbow-yellow ${selectedReport === 'fridgeRentals' ? 'active' : ''}`}
            onClick={() => setSelectedReport('fridgeRentals')}
          >
            <h3>Fridge Rentals</h3>
            <p>View all campers who have requested a fridge rental</p>
          </div>
          <div 
            className={`report-card rainbow-green ${selectedReport === 'sharedAccommodations' ? 'active' : ''}`}
            onClick={() => setSelectedReport('sharedAccommodations')}
          >
            <h3>Shared Accommodations</h3>
            <p>View all accommodations that have multiple families registered, grouped by place code and sorted by start date</p>
          </div>
          <div 
            className={`report-card rainbow-blue ${selectedReport === 'pendingRegistrations' ? 'active' : ''}`}
            onClick={() => setSelectedReport('pendingRegistrations')}
          >
            <h3>Pending Registrations</h3>
            <p>View all registrations that are pending approval</p>
          </div>
          <div 
            className={`report-card rainbow-purple ${selectedReport === 'partialAvailability' ? 'active' : ''}`}
            onClick={() => setSelectedReport('partialAvailability')}
          >
            <h3>Partial Availability</h3>
            <p>View cottages booked for less than 5 nights - showing potential for additional bookings</p>
          </div>
          <div 
            className={`report-card rainbow-red ${selectedReport === 'paymentRecords' ? 'active' : ''}`}
            onClick={() => setSelectedReport('paymentRecords')}
          >
            <h3>Payment Records</h3>
            <p>View all payment records with filtering by payment type, search, and sorting capabilities</p>
          </div>
          <div
            className={`report-card rainbow-purple ${selectedReport === 'parBalances' ? 'active' : ''}`}
            onClick={() => setSelectedReport('parBalances')}
          >
            <h3>PAR Balances</h3>
            <p>List campers with PAR available and/or PAR spent, including total PAR left</p>
          </div>


          <div 
            className={`report-card rainbow-orange ${selectedReport === 'newCamperReport' ? 'active' : ''}`}
            onClick={() => setSelectedReport('newCamperReport')}
          >
            <h3>New Camper Report</h3>
            <p>View all campers where the NewCamper field is set to True</p>
          </div>
          <div 
            className={`report-card rainbow-yellow ${selectedReport === 'accountsReceivable' ? 'active' : ''}`}
            onClick={() => setSelectedReport('accountsReceivable')}
          >
            <h3>Accounts Receivable</h3>
            <p>List all campers with a non-zero balance (including credits)</p>
          </div>
          <div 
            className={`report-card rainbow-green ${selectedReport === 'campMasterList' ? 'active' : ''}`}
            onClick={() => setSelectedReport('campMasterList')}
          >
            <h3>Camp Master List</h3>
            <p>View all registered campers, their place name, and dates attending</p>
          </div>
          <div 
            className={`report-card rainbow-blue ${selectedReport === 'youthBreakDown' ? 'active' : ''}`}
            onClick={() => setSelectedReport('youthBreakDown')}
          >
            <h3>Youth Break Down</h3>
            <p>List all campers with age category "YouthChildren" including sub category and place name</p>
          </div>
          <div 
            className={`report-card rainbow-purple ${selectedReport === 'arrived' ? 'active' : ''}`}
            onClick={() => setSelectedReport('arrived')}
          >
            <h3>Arrived</h3>
            <p>Track camper arrival status with toggle between arrived/not arrived and real-time updates</p>
          </div>
          <div 
            className={`report-card rainbow-red ${selectedReport === 'treasurersAnnualReport' ? 'active' : ''}`}
            onClick={() => setSelectedReport('treasurersAnnualReport')}
          >
            <h3>Treasurer's Annual Report</h3>
            <p>Financial Summary showing income breakdown and payment status</p>
          </div>
          <div 
            className={`report-card rainbow-red ${selectedReport === 'tempFinancialSummary' ? 'active' : ''}`}
            onClick={() => setSelectedReport('tempFinancialSummary')}
          >
            <h3>Temp Financial Summary</h3>
            <p>Temporary copy of the old stored-fee Financial Summary for comparison</p>
          </div>
          <div 
            className={`report-card rainbow-orange ${selectedReport === 'donations' ? 'active' : ''}`}
            onClick={() => setSelectedReport('donations')}
          >
            <h3>Donations</h3>
            <p>View all campers with donations over $20, including contact information</p>
          </div>
          <div 
            className={`report-card rainbow-green ${selectedReport === 'otherFees' ? 'active' : ''}`}
            onClick={() => setSelectedReport('otherFees')}
          >
            <h3>Custom Other Fees</h3>
            <p>List all custom other fees with notes and descriptions from all registrations</p>
          </div>
          <div 
            className={`report-card rainbow-purple ${selectedReport === 'programInterests' ? 'active' : ''}`}
            onClick={() => setSelectedReport('programInterests')}
          >
            <h3>Program Interests</h3>
            <p>View campers enrolled in Nursery, VBS, Youth Group, Cruise programs or interested in volunteering</p>
          </div>
          <div 
            className={`report-card rainbow-yellow ${selectedReport === 'mealTickets' ? 'active' : ''}`}
            onClick={() => setSelectedReport('mealTickets')}
          >
            <h3>Meal Tickets</h3>
            <p>Pre-registration manifest for issuing meal passes - lists all purchased meal tickets by camper</p>
          </div>
          <div 
            className={`report-card rainbow-blue ${selectedReport === 'primaryCampersContact' ? 'active' : ''}`}
            onClick={() => setSelectedReport('primaryCampersContact')}
          >
            <h3>Primary Campers Contact</h3>
            <p>Contact list of all primary campers with phone, email, and address information</p>
          </div>
          <div
            className={`report-card rainbow-green ${selectedReport === 'cabinUtilization' ? 'active' : ''}`}
            onClick={() => setSelectedReport('cabinUtilization')}
          >
            <h3>Cabin Utilization</h3>
            <p>View camper counts and capacity for cottages and private cottages</p>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!selectedReport) {
      return renderReportsList();
    }

    if (selectedReport === 'logs') {
      return renderLogsList();
    }

    if (selectedReport === 'email') {
      if (emailLogsLoading) {
        return <div>Loading email logs...</div>;
      }
      if (emailLogsError) {
        return <div className="error">{emailLogsError}</div>;
      }
      return renderEmailLogs();
    }

    if (selectedReport === 'securityLogs') {
      return <SecurityLogs />;
    }

    if (selectedReport === 'submissionErrorLogs') {
      return <SubmissionErrorLogs />;
    }



    if (selectedReport === 'attendanceStats') {
      return <AttendanceStats />;
    }

    if (selectedReport === 'fridgeRentals') {
      return <FridgeRentals />;
    }

    if (selectedReport === 'sharedAccommodations') {
      return <SharedAccommodations />;
    }

    if (selectedReport === 'pendingRegistrations') {
      return <PendingRegistrations />;
    }

    if (selectedReport === 'partialAvailability') {
      return <PartialAvailability />;
    }

    if (selectedReport === 'paymentRecords') {
      return <PaymentRecords />;
    }

    if (selectedReport === 'etransferInbox') {
      return <EtransferInboxReport />;
    }

    if (selectedReport === 'parBalances') {
      return <ParBalancesReport />;
    }

    if (selectedReport === 'accountsReceivable') {
      return <AccountsReceivable />;
    }

    if (selectedReport === 'campMasterList') {
      return <CampMasterList />;
    }

    if (selectedReport === 'youthBreakDown') {
      return <YouthBreakDown />;
    }

    if (selectedReport === 'arrived') {
      return <Arrived />;
    }

    if (selectedReport === 'newCamperReport') {
      return <NewCamperReport />;
    }

    if (selectedReport === 'treasurersAnnualReport') {
      return <TreasurersAnnualReport />;
    }

    if (selectedReport === 'tempFinancialSummary') {
      return <TempFinancialSummary />;
    }

    if (selectedReport === 'donations') {
      return <Donations />;
    }

    if (selectedReport === 'otherFees') {
      return <OtherFeesReport />;
    }

    if (selectedReport === 'programInterests') {
      return <ProgramInterests />;
    }

    if (selectedReport === 'mealTickets') {
      return <MealTickets />;
    }

    if (selectedReport === 'primaryCampersContact') {
      return <PrimaryCampersContact />;
    }

    if (selectedReport === 'cabinUtilization') {
      return <CabinUtilization />;
    }

    return null;
  };

  return (
    <div>
      <div className="reports">
        <div className="reports-header">
          <h1>{selectedReport === 'etransferInbox' ? 'E-Transfer Inbox' : 'Reports'}</h1>
          {selectedReport && (
            <button 
              className="back-to-reports"
              onClick={() => {
                if (selectedReport === 'etransferInbox') {
                  navigate('/admin/assignments');
                } else if (selectedReport === 'email' || selectedReport === 'securityLogs' || selectedReport === 'submissionErrorLogs') {
                  setSelectedReport('logs');
                } else {
                  setSelectedReport(null);
                }
              }}
            >
              {selectedReport === 'etransferInbox'
                ? '← Back to Assignment List'
                : selectedReport === 'email' || selectedReport === 'securityLogs' || selectedReport === 'submissionErrorLogs'
                ? '← Back to Logs'
                : '← Back to Reports List'}
            </button>
          )}
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default Reports; 